// `react-native` on web = react-native-web, with three adjustments:
//
//   - `Alert` replaced by the browser implementation in ../shell/webAlert
//     (RNW ships Alert as a no-op).
//   - `Dimensions` / `useWindowDimensions` clamped to the app canvas
//     (../shell/dimensions.ts) so screens measure the width they are drawn in.
//   - `StyleSheet.create` gives the app's content sheet matching bottom
//     corners on web (../shell/sheetStyle.ts) and puts the lock/unlock
//     template's pinned blocks into flow on wide frames
//     (../shell/lockUnlockStyle.ts), does the same for the letter hook's
//     footer (../shell/letterHookStyle.ts), and hides the phone's hamburger menu
//     button where the sidebar replaces it (../shell/hamburgerStyle.ts).
//   - `ScrollView` lets the page scroll on the desktop frames
//     (../shell/DocScrollView.tsx).
//   - `PermissionsAndroid` stubbed for Android-only code paths.
//
// A local export shadows the same name from `export *`, so the names below win.

import { useMemo } from 'react';
import {
  Dimensions as RNWDimensions,
  StyleSheet as RNWStyleSheet,
  useWindowDimensions as rnwUseWindowDimensions,
} from 'react-native-web';

import { clampDims } from '../shell/dimensions';
import { hideHamburgerOnDesktop } from '../shell/hamburgerStyle';
import { relayoutLetterHook } from '../shell/letterHookStyle';
import { relayoutLockUnlock } from '../shell/lockUnlockStyle';
import { roundSheets } from '../shell/sheetStyle';

export * from 'react-native-web';
export { Alert } from '../shell/webAlert';
export { ScrollView } from '../shell/DocScrollView';

// ---------------------------------------------------------------------------
// Dimensions, clamped to the canvas
// ---------------------------------------------------------------------------

type DimKey = 'window' | 'screen';
type ChangeHandler = (e: { window: unknown; screen: unknown }) => void;

export const Dimensions = {
  get(key: DimKey) {
    return clampDims(RNWDimensions.get(key));
  },
  set(dims: Parameters<typeof RNWDimensions.set>[0]) {
    return RNWDimensions.set(dims);
  },
  addEventListener(type: 'change', handler: ChangeHandler) {
    const wrapped = (e: { window: Parameters<typeof clampDims>[0]; screen: Parameters<typeof clampDims>[0] }) =>
      handler({ window: clampDims(e.window), screen: clampDims(e.screen) });
    return RNWDimensions.addEventListener(type, wrapped);
  },
  removeEventListener(type: 'change', handler: ChangeHandler) {
    // RNW returns a subscription from addEventListener; callers that use
    // the legacy remove API get a best-effort no-op here.
    void type;
    void handler;
  },
};

export function useWindowDimensions() {
  const d = rnwUseWindowDimensions();
  return useMemo(() => clampDims(d), [d.width, d.height, d.scale, d.fontScale]);
}

// ---------------------------------------------------------------------------
// StyleSheet, with the web sheet treatment
// ---------------------------------------------------------------------------

export const StyleSheet = Object.assign({}, RNWStyleSheet, {
  create<T extends Record<string, unknown>>(styles: T): T {
    return RNWStyleSheet.create(hideHamburgerOnDesktop(relayoutLetterHook(relayoutLockUnlock(roundSheets(styles))))) as T;
  },
});

// ---------------------------------------------------------------------------
// PermissionsAndroid
// ---------------------------------------------------------------------------

// Only ever called behind `Platform.OS === 'android'`, so on web this is never
// reached; the stub keeps the import resolvable and grants if it somehow is.
const RESULTS = {
  GRANTED: 'granted',
  DENIED: 'denied',
  NEVER_ASK_AGAIN: 'never_ask_again',
} as const;

export const PermissionsAndroid = {
  RESULTS,
  PERMISSIONS: new Proxy({} as Record<string, string>, {
    get: (_t, key) => `android.permission.${String(key)}`,
  }),
  async request(): Promise<string> {
    return RESULTS.GRANTED;
  },
  async check(): Promise<boolean> {
    return true;
  },
  async requestMultiple(perms: string[]): Promise<Record<string, string>> {
    return Object.fromEntries(perms.map((p) => [p, RESULTS.GRANTED]));
  },
};
